import _ from "lodash";
import React, { useState } from "react";
import classNames from "classnames";
import classes from "./FilterHeader.scss";
import I18nHOC from "UIComponents/I18nHOC";
import { compose } from "lib/apolloClient";
import {
  CloseOutlined,
  InformationFilled,
  SettingsOutlined,
} from "@toddle-edu/ds-icons";
import {
  Button,
  FilterLabelButton,
  ChecklistDropdown,
  SearchInput,
} from "@toddle-edu/ds-web";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import { columnLabels, getSortedArrayMemoized } from "NodeEditor/Utils";
import {
  ELEMENT_TYPE_DP_SYLLABUS,
  ELEMENT_TYPE_DP_KNOWLEDGE_FRAMEWORK,
} from "Constants/stringConstants";

const FilterHeader = props => {
  const {
    filters,
    updateFilters,
    gradeOptions,
    organizationTagsObjectMap,
    draftId,
    mode,
    loginActiveTab,
    showWarningText,
    configuredColumns,
    openedFrom,
    onUpdateSettings,
    disableFilters,
    t,
    nodeType,
    subjectGroupType,
    testId,
  } = props;

  const [isMessageOpen, setIsWarningMessageOpen] = useState(true);

  const toggleIsMessageOpen = () =>
    setIsWarningMessageOpen(isMessageOpen => !isMessageOpen);

  const handleChangeSearchTerm = value =>
    updateFilters({
      key: "searchText",
      data: value,
    });
  const handleChangeGrades = value =>
    updateFilters({
      key: "grades",
      data: value,
    });
  const handleChangeTags = value =>
    updateFilters({
      key: "tags",
      data: value,
    });

  const handleColumnsChange = value => {
    onUpdateSettings({ value });
  };

  const getSelectedOptionsLabels = ({ options, value }) => {
    const selectedOptionsLabels = _.map(
      _.filter(options, item => _.includes(value, item.value)),
      option => option.label
    );

    return selectedOptionsLabels;
  };

  const configureColumnsDropdown = !_.isEmpty(
    columnLabels({ t, nodeType, subjectGroupType })
  ) && {
    id: "configure_columns",
    options: columnLabels({ t, nodeType, subjectGroupType }),
    label: "configure_columns",
    value: configuredColumns,
    onChange: handleColumnsChange,
    isCheckList: true,
    hideValue: true,
    buttonComponent: (
      <Button
        icon={<SettingsOutlined />}
        size={"small"}
        variant={"plain"}
        testId={`${testId}-configure-columns`}
      >
        {t("common:configure_columns")}
      </Button>
    ),
  };

  const FILTER_DROPDOWN_CONFIG = [
    {
      id: "grades",
      options: getSortedArrayMemoized({ inputObject: gradeOptions }),
      value: filters.grades,
      onChange: handleChangeGrades,
      showAllOption: true,
      noResultLabel: t("common:no_label_found", {
        label: t("common:lowercase", {
          text: t("common:grade_plural"),
        }),
      }),
      hide: [ELEMENT_TYPE_DP_SYLLABUS, ELEMENT_TYPE_DP_KNOWLEDGE_FRAMEWORK],
      buttonComponent: (
        <FilterLabelButton
          size={"small"}
          variant={"plain"}
          label={t("common:grade_plural")}
          labelValue={getLabelValueForMultiFilterLabelButton({
            options: getSortedArrayMemoized({ inputObject: gradeOptions }),
            selectedOptions: getSelectedOptionsLabels({
              options: gradeOptions,
              value: filters.grades,
            }),
            t,
            allText: t("common:all_label", {
              label: t("common:grade_plural"),
            }),
            noneText: t("common:none"),
          })}
          testId={`${testId}-grade-filter`}
        />
      ),
      isSearchEnabled: true,
    },
    {
      id: "tags",
      options: getSortedArrayMemoized({
        inputObject: organizationTagsObjectMap,
      }),
      value: filters.tags,
      onChange: handleChangeTags,
      showAllOption: true,
      noResultLabel: t("common:no_label_found", {
        label: t("common:lowercase", {
          text: t("common:tags"),
        }),
      }),
      hide: [ELEMENT_TYPE_DP_KNOWLEDGE_FRAMEWORK],
      hideSubjectGroup: ["CP_CORE"],
      buttonComponent: (
        <FilterLabelButton
          size={"small"}
          variant={"plain"}
          label={t("common:tags")}
          labelValue={getLabelValueForMultiFilterLabelButton({
            options: getSortedArrayMemoized({
              inputObject: organizationTagsObjectMap,
            }),
            selectedOptions: getSelectedOptionsLabels({
              options: organizationTagsObjectMap,
              value: filters.tags,
            }),
            t,
            allText: t("common:all_label", {
              label: t("common:tags"),
            }),
            noneText: t("common:none"),
          })}
          testId={`${testId}-tags-filter`}
        />
      ),
      isSearchEnabled: true,
    },
  ];

  let finalFilters = FILTER_DROPDOWN_CONFIG;
  if (openedFrom && configureColumnsDropdown)
    finalFilters = [...finalFilters, configureColumnsDropdown];

  const renderFilterDropDowns = _.map(finalFilters, filterDropdownProps => {
    if (
      !_.includes(_.get(filterDropdownProps, "hide", []), nodeType) &&
      !_.includes(
        _.get(filterDropdownProps, "hideSubjectGroup", []),
        subjectGroupType
      )
    ) {
      const { buttonComponent, ...rest } = filterDropdownProps;
      return (
        <React.Fragment key={filterDropdownProps.id}>
          <ChecklistDropdown
            {...rest}
            disabled={disableFilters}
            checklistContainerClassName={classes.dropdownClasses}
            testId={testId}
          >
            {buttonComponent}
          </ChecklistDropdown>
        </React.Fragment>
      );
    }
  });

  const warningText = (
    <div className={classes.warningContainer}>
      <div className={classes.svgContainerWarning}>
        <InformationFilled variant="warning" />
      </div>
      <div className={classes.warningText}>
        {t("scopeAndSequence:draft_exists_text")}
      </div>
      <div className={classes.svgContainerClose} onClick={toggleIsMessageOpen}>
        <CloseOutlined size={"xx-small"} />
      </div>
    </div>
  );

  const isWarningMessageOpen =
    draftId &&
    showWarningText &&
    mode == "view" &&
    _.includes(["admin", "school-onboarding"], loginActiveTab) &&
    isMessageOpen;

  const getPlaceHoldersText = () => {
    const { plannerElementSetLabel } = props;

    if (
      _.includes(
        ["DP_SYLLABUS", "DP_SUBJECT_STANDARD", "DP_KNOWLEDGE_FRAMEWORK"],
        nodeType
      )
    ) {
      return t("common:search");
    }

    return t("academicSetup:search_for_module", {
      moduleName: plannerElementSetLabel,
    });
  };

  const isOpenedFromProgressReport = openedFrom == "PROGRESS_REPORT";

  const searchContainerClasses = classNames({
    [classes.searchContainer]: true,
    [classes.progressReportSearchContainer]: isOpenedFromProgressReport,
  });

  return (
    <div className={classes.container}>
      <div className={classes.filtersContainer}>
        <div className={searchContainerClasses}>
          <SearchInput
            value={filters.searchText}
            placeholder={getPlaceHoldersText()}
            onChange={handleChangeSearchTerm}
            size={"large"}
            testId={testId}
            autoFocus={true}
          />
        </div>
        <div className={classes.rightContainer}>{renderFilterDropDowns}</div>
      </div>
      {isWarningMessageOpen && warningText}
    </div>
  );
};

export default compose(
  I18nHOC({
    resource: ["academicSetup", "common", "scopeAndSequence"],
  })
)(FilterHeader);
